import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { UserService } from '../../services/user.service';
import { ClosedUserWithBalance, PaginatedClosedUsersWithBalance } from '../../interfaces';
import { generatePagination } from '../../utils/pagination.utils';

@Component({
  selector: 'app-closed-users-with-balance',
  standalone: false,
  templateUrl: './closed-users-with-balance.component.html',
  styleUrl: './closed-users-with-balance.component.css',
})
export class ClosedUsersWithBalanceComponent implements OnInit, OnDestroy {
  closedUsers: ClosedUserWithBalance[] = [];
  nextPageUrl: string | null = null;
  previousPageUrl: string | null = null;
  currentPage = 1;
  pageSize = 20;
  totalCount = 0;
  totalPages = 1;
  pageNumbers: (number | string)[] = [];
  searchQuery: string = '';

  isLoading = false;
  hasError = false;
  errorMessage = '';

  copiedUuid: string | null = null;
  private copyTimeout: any = null;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private userService: UserService) {}

  ngOnInit(): void {
    this.searchSubject
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.currentPage = 1;
        this.loadClosedUsers(1);
      });

    this.loadClosedUsers(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
  }

  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.currentPage = 1;
    this.loadClosedUsers(1);
  }

  loadClosedUsers(page: number): void {
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    this.userService
      .getClosedUsersWithBalance(page, this.pageSize, this.searchQuery)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: PaginatedClosedUsersWithBalance) => {
          this.closedUsers = response.results || [];
          this.totalCount = response.count || 0;
          this.nextPageUrl = response.next;
          this.previousPageUrl = response.previous;
          this.currentPage = page;
          this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
          this.pageNumbers = generatePagination(this.currentPage, this.totalPages);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error fetching closed users with balance:', err);
          this.hasError = true;
          this.errorMessage =
            err?.error?.message ||
            err?.message ||
            'Unable to fetch closed users with balance. Please try again.';
          this.isLoading = false;
          this.closedUsers = [];
        },
      });
  }

  goToNextPage(): void {
    if (this.nextPageUrl && !this.isLoading) {
      const nextPage = this.getPageNumber(this.nextPageUrl) || this.currentPage + 1;
      this.loadClosedUsers(nextPage);
    }
  }

  goToPreviousPage(): void {
    if (this.previousPageUrl && !this.isLoading) {
      const prevPage = this.getPageNumber(this.previousPageUrl) || this.currentPage - 1;
      this.loadClosedUsers(prevPage);
    }
  }

  goToPage(page: number | string): void {
    if (typeof page === 'number' && page !== this.currentPage && !this.isLoading) {
      this.loadClosedUsers(page);
    }
  }

  private getPageNumber(url: string): number | null {
    if (!url) return null;
    const queryString = url.split('?')[1];
    if (!queryString) return null;
    const params = new URLSearchParams(queryString);
    const page = params.get('page');
    return page ? Number(page) : null;
  }

  copyToClipboard(text: string, event: Event): void {
    event.stopPropagation();
    if (!text) return;

    navigator.clipboard.writeText(text).then(() => {
      this.copiedUuid = text;
      if (this.copyTimeout) {
        clearTimeout(this.copyTimeout);
      }
      this.copyTimeout = setTimeout(() => {
        this.copiedUuid = null;
      }, 2000);
    });
  }

  formatAmount(amount: string | number): string {
    if (amount === undefined || amount === null || amount === '') return '0.00';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    });
  }

  getProviderClass(provider: string): string {
    const p = (provider || '').toUpperCase();
    if (p.includes('BRIDGE')) {
      return 'bg-blue-50 text-blue-700 border-blue-200';
    } else if (p.includes('NOAH')) {
      return 'bg-purple-50 text-purple-700 border-purple-200';
    } else if (p.includes('PRIVY')) {
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    }
    return 'bg-gray-100 text-gray-700 border-gray-200';
  }

  getClosureReasonClass(reason: string): string {
    const r = (reason || '').toUpperCase();
    if (r.includes('FRAUD') || r.includes('RISK') || r.includes('SANCTION') || r.includes('BLACKLIST')) {
      return 'bg-red-50 text-red-700 border-red-200';
    } else if (r.includes('REQUEST') || r.includes('USER')) {
      return 'bg-amber-50 text-amber-700 border-amber-200';
    } else if (r.includes('INACTIVE') || r.includes('DORMANT')) {
      return 'bg-gray-100 text-gray-700 border-gray-200';
    }
    return 'bg-orange-50 text-orange-700 border-orange-200';
  }
}
